import { useMsal } from '@azure/msal-react';
import { useRef, useEffect, useState } from 'react';
import CustomSelect from '../inputs/CustomSelect';
import '../css/Header.css';
import '../css/Button.css';
import { useNavigation } from '../function/navigation';
import WriteFeedback from './WriteFeedback';

function Header({
  inputs,
  setRegion,
  pdcLoaded,
  crmInfo,
  setCrmInfo,
  unitOptions,
  setUnit,
  hidePhoneMenu = false,
  hasAllHeights = true,
}) {
  const { nav, dispatch } = useNavigation();

  const notProdEnv = process.env.REACT_APP_ENV !== 'production';
  const { instance } = useMsal();
  const isPhone = typeof window !== 'undefined' && window.innerWidth < 800;
  const perfClass = pdcLoaded?.performance
    ? 'performance'
    : pdcLoaded?.id === 0
      ? 'customized'
      : '';
  const [ok, setOk] = useState(false);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const userLevels = [
    'Layout Engineer',
    'Customer',
    'Customer plus',
    'IAM',
    'Application Engineer',
    'Specialists',
    'Admin',
  ];

  const regionName = {
    1: 'EMEA (EUR)',
    2: 'AMCAS (USD)',
    3: 'APAC (CNY)',
    4: 'APAC (EUR)',
  };

  const regionChoice =
    crmInfo?.is_admin === true
      ? Object.keys(regionName).map((key) => {
          return { id: key, name: regionName[key] };
        })
      : [3, 4].includes(inputs.region)
        ? [3, 4].map((key) => {
            return { id: key, name: regionName[key] };
          })
        : null;

  useEffect(() => {
    const handleClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
        dispatch({ type: 'feedbackPanelOpen', payload: false });
      }
    };
    document.addEventListener('mousedown', handleClick, { passive: true });
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dispatch]);

  useEffect(() => {
    if (nav.feedbackPanelOpen) {
      setUserMenuOpen(true);
    }
  }, [nav.feedbackPanelOpen]);

  useEffect(() => {
    const header = document.querySelector('.app-header');
    if (!header) return;

    const setHeaderHeight = () => {
      const h = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        '--app-header-height',
        `${h}px`
      );
    };

    const ro = new ResizeObserver(setHeaderHeight);
    ro.observe(header);
    setHeaderHeight();

    return () => ro.disconnect();
  }, [pdcLoaded, ok]);

  const handleSignOut = () => {
    const acc = instance.getActiveAccount();
    instance.logoutPopup({ logoutHint: acc?.idTokenClaims?.login_hint });
  };

  const welcome = (
    <div className="textbox">
      <p>Welcome {instance.getActiveAccount().name}</p>
      <p className="prj-text">
        {crmInfo?.opportunity_number
          ? `Loaded project: ${crmInfo.opportunity_number}`
          : crmInfo?.budget?.name
            ? `Budget project: ${crmInfo.budget.name}`
            : 'No project loaded'}
      </p>
    </div>
  );

  const views = [
    { key: 'menu', label: 'Menu' },
    { key: 'params', label: 'Parameters' },
    { key: 'spiral', label: 'Spiral' },
    { key: 'summary', label: 'Summary' },
  ];
  const phoneViews = hasAllHeights
    ? views
    : views.filter((v) => v.key === 'params' || v.key === 'spiral');

  return (
    <header className={`app-header ${perfClass}`}>
      {notProdEnv && !ok && (
        <div className="dev-warning">
          <p className="env-warning">
            ⚠️ This is a {process.env.REACT_APP_ENV} environment. This
            application is not connected to production systems or data.
          </p>
          <button
            className="button-sm button-secondary light"
            onClick={() => setOk(true)}
          >
            OK
          </button>
        </div>
      )}
      <div className="header-top">
        <div className="header-left">
          <div className="logo-container">
            <img
              className="app-logo"
              src={`${process.env.PUBLIC_URL}/${isPhone ? 'ambaflex_icon_white' : 'ambaflex_logo_full_size'}.png`}
              alt="Logo"
            />
          </div>
        </div>
        {!isPhone && (
          <h3
            className={`pdc-loaded ${perfClass} light`}
            onClick={() => dispatch({ type: 'mainPage', payload: 'pdcPage' })}
          >
            {pdcLoaded !== undefined && pdcLoaded.name}
          </h3>
        )}
        <div className="user-container" ref={userMenuRef}>
          {welcome}
          <img
            src={`${process.env.PUBLIC_URL}/icons/user.png`}
            className="user-icon"
            alt="user"
            onClick={() => setUserMenuOpen((o) => !o)}
          />
          {userMenuOpen && (
            <div className="user-dropdown">
              {unitOptions && (
                <div className="select-level">
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/unit.png`}
                    className="unit-icon"
                    alt="unit"
                  />
                  <CustomSelect
                    value={inputs.unit_system}
                    onChange={setUnit}
                    disabled={false}
                    optionChoice={unitOptions}
                  />
                </div>
              )}
              <div
                className={`select-level ${regionChoice === null ? 'disabled' : ''}`}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/icons/region.png`}
                  className="unit-icon"
                  alt="region"
                />
                {regionChoice === null ? (
                  regionName[inputs.region] || ''
                ) : (
                  <CustomSelect
                    value={inputs.region}
                    onChange={setRegion}
                    disabled={false}
                    optionChoice={regionChoice}
                  />
                )}
              </div>
              {crmInfo?.is_admin === true && (
                <div className="select-level">
                  <CustomSelect
                    value={crmInfo?.user_level}
                    onChange={(value) =>
                      setCrmInfo({
                        user_level: value,
                      })
                    }
                    disabled={false}
                    optionChoice={userLevels}
                  />
                </div>
              )}
              {!nav.feedbackPanelOpen ? (
                <button
                  className="button-sm button-gray"
                  onClick={() =>
                    dispatch({ type: 'feedbackPanelOpen', payload: true })
                  }
                >
                  Give Feedback
                </button>
              ) : (
                <WriteFeedback
                  username={instance.getActiveAccount().username}
                  inputs={inputs}
                  onClose={() =>
                    dispatch({ type: 'feedbackPanelOpen', payload: false })
                  }
                />
              )}
              <button
                className="button-sm button-primary"
                onClick={handleSignOut}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      {isPhone && (
        <>
          <div
            className={`pdc-loaded ${perfClass}`}
            onClick={() => dispatch({ type: 'mainPage', payload: 'pdcPage' })}
          >
            {pdcLoaded !== undefined && pdcLoaded.name}
          </div>
          {!hidePhoneMenu && (
            <div className="header-phone-views">
              {phoneViews.map(({ key, label }) => (
                <button
                  key={key}
                  className={`button-sm button-secondary ${nav.phoneMenu === key ? 'active' : ''}`}
                  onClick={() => {
                    dispatch({ type: 'phoneMenu', payload: key });
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </header>
  );
}

export default Header;
